import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ExternalLink, Loader2, LayoutTemplate, Users } from "lucide-react";
import { LANDINGS } from "../data/landings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LandingsAdmin() {
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("sf-admin-token") || "";
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/landing-stats`, auth);
      setStats(data.by_fonte || {});
    } catch {
      toast.error("Errore nel caricamento delle statistiche landing");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const landings = Object.values(LANDINGS);
  const totalLeads = landings.reduce((acc, l) => acc + (stats[l.fonte] || 0), 0);

  return (
    <div className="space-y-6" data-testid="landings-admin">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-serif font-bold text-3xl">Pagine landing</h1>
          <p className="text-sm text-ink/50 mt-1">
            {landings.length} pagine pubblicate · {totalLeads} richieste ricevute dalle landing
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 size={28} className="animate-spin text-azure" />
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-ink/10 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ink/10 text-left text-xs uppercase tracking-wider text-ink/45">
                <th className="px-5 py-3.5 font-semibold">Pagina</th>
                <th className="px-5 py-3.5 font-semibold">Indirizzo</th>
                <th className="px-5 py-3.5 font-semibold">Stato</th>
                <th className="px-5 py-3.5 font-semibold">Richieste</th>
                <th className="px-5 py-3.5 font-semibold text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {landings.map((l) => (
                <tr key={l.slug} className="border-b border-ink/5 last:border-0 hover:bg-paper/60 transition-colors" data-testid={`landing-row-${l.slug}`}>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-azure/10 text-brandblue shrink-0">
                        <LayoutTemplate size={16} />
                      </span>
                      <div className="min-w-0">
                        <p className="font-semibold text-ink truncate max-w-[320px]">{l.eyebrow}</p>
                        <p className="text-xs text-ink/45 truncate max-w-[320px]">{l.h1}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <span className="inline-block rounded-lg bg-ink/5 px-2.5 py-1 text-xs font-mono text-ink/70" data-testid={`landing-url-${l.slug}`}>
                      /{l.slug}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 text-green-700 text-[11px] font-semibold px-2.5 py-1" data-testid={`landing-status-${l.slug}`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500" /> Pubblicata
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <span className="inline-flex items-center gap-1.5 font-semibold text-ink" data-testid={`landing-leads-${l.slug}`}>
                      <Users size={14} className="text-ink/40" />
                      {stats[l.fonte] || 0}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <a
                      href={`/${l.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`landing-open-${l.slug}`}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-azure hover:text-brandblue border border-azure/30 hover:border-azure rounded-full px-3.5 py-1.5 transition-colors"
                    >
                      <ExternalLink size={12} /> Apri pagina
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-ink/40">
        Le pagine landing sono definite nel codice del sito: per modificarne i testi o crearne di nuove, chiedi all'assistente.
        Le "Richieste" contano i contatti arrivati dal form di ciascuna landing.
      </p>
    </div>
  );
}
